/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Badge,
  Box,
  Chip,
  FormHelperText,
  Grid,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  Tooltip,
} from "@mui/material";
import { AddCircleOutline, InfoOutlined, RemoveCircleOutline } from "@mui/icons-material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useAppState } from "../../../context/StateContext";
import { AutomaticSequencing } from "../../../models/automatic.sequencing";
import { Branch } from "../../../models/branch";
import { Error } from "../../../models/error";
import { Measurement } from "../../../models/measurement";
import { Office } from "../../../models/office";
import { Project } from "../../../models/project";
import { ShiftTime } from "../../../models/shift.time";
import { Timezone } from "../../../models/timezone";
import { Translate } from "../../../models/translate.type";
import { Roles, User } from "../../../models/user";
import BranchService from "../../../services/branch.service";
import MeasurementService from "../../../services/measurement.service";
import OfficeService from "../../../services/office.service";
import ProjectService from "../../../services/project.service";
import RoleService from "../../../services/role.service";
import TimezoneService from "../../../services/timezone.service";
import TranslateService from "../../../services/translate.service";
import UserService from "../../../services/user.service";
import { checkError } from "../../../util/checkError";
import { CompanyProfile, MeasurementType, Status, UserTypes } from "../../../util/enums";
import { formatGMT } from "../../../util/Format";
import { isRetailerPlus } from "../../../util/validation";
import AutoSuggest from "../../Form/AutoSuggest";
import RadioGroup from "../../Form/RadioGroup";
import Select from "../../Form/Select";
import SelectMultiple, { SelectMultipleCount } from "../../Form/SelectMultiple";
import SkeletonComponent from "../Skeleton";
import { NotificationToast } from "../Toast/NotificationToast";
import ContactDetailsComponent from "./components/ContactDetails";
// import { ProjectList } from "./components/ProjectList";
import ShiftTimes from "./components/ShiftTimes";
import UserAddressComponent from "./components/UserAddress";
import UserDetailsComponent from "./components/UserDetails";
import useStyles, {
  LeftTextContainer,
  PasswordContainer,
  StyledImage,
  TextLeft,
  TextLeftBottom,
  TextRight,
  TextRightContainer,
} from "./styles";
import UserPreferencesComponent from "./components/UserPreferences";

const KEY_ICON = require("../../../assets/img/key.svg");

interface Props {
  isOnProfilePage?: boolean;
  showingFromPeopleSettings?: boolean;
  isLoading?: boolean;
  userInformation?: User;
  isRegisteringUser?: boolean;
  view?: boolean;
  errors?: yup.ValidationError[];
  sequence?: AutomaticSequencing;
  updateOrCreate: (value: User) => void;
  errorResponse?: Error;
}

const TooltipRoleName = ({ title, child }: { title: string; child: React.ReactNode }) => {
  const classes = useStyles();

  return title.length > 0 ? (
    <Tooltip title={title} arrow className={classes.tooltip}>
      <Badge badgeContent={<InfoOutlined className={classes.tooltipIcon} />}>{child}</Badge>
    </Tooltip>
  ) : (
    <>{child}</>
  );
};

const SettingsUserForm = ({
  isOnProfilePage = false,
  view = false,
  showingFromPeopleSettings = true,
  userInformation,
  errors,
  isLoading = false,
  isRegisteringUser,
  sequence,
  updateOrCreate,
  errorResponse,
}: Props) => {
  const classes = useStyles();
  const { currentUser, isFMVersion, isSPVersion } = useAppState();
  const { t: translate } = useTranslation();

  const [rolesList, setRolesList] = useState<Roles[]>();
  const [timezones, setTimezones] = useState<Timezone[]>();
  const [translations, setTranslations] = useState<Translate[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<Branch[]>([]);

  const [dimensions, setDimensions] = useState<Measurement[]>([]);
  const [weights, setWeights] = useState<Measurement[]>([]);

  const [timezone, setTimezone] = useState<string>("");
  const [language, setLanguage] = useState<string>("");
  const [isUsingCompanyAddress, setUsingCompanyAddress] = useState<boolean>(false);
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [officeList, setOfficeList] = useState<Office[]>([]);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const { isFetching: isFetchingDimensions } = useQuery(
    [`measurement.${MeasurementType.DIMENSION}.list`],
    () =>
      MeasurementService.getAll({
        status: Status.ACTIVE,
        type: MeasurementType.DIMENSION,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!(isOnProfilePage && currentUser?.companyId),
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setDimensions(response.data);
        }
      },
    }
  );

  const { mutate: forgotPassword } = useMutation(
    ["users.forgot.password"],
    () => UserService.forgotPassword(userInformation?.email ?? ""),
    {
      onSuccess: (response: AxiosResponse<{ success: boolean; message?: string } | boolean>) => {
        if (response?.data && typeof response.data === "object") {
          if (!response.data?.["success"]) {
            NotificationToast.error(response?.data?.["message"] || translate("couldNotSendEmail"));
          } else {
            NotificationToast.success(translate("emailSuccessfullySent"), { duration: 5000 });
          }
        } else if (response?.data) {
          NotificationToast.success(translate("emailSuccessfullySent"), { duration: 5000 });
        }
      },
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("couldNotSendEmail"));
      },
    }
  );
  const { isFetching: isFetchingWeights } = useQuery(
    [`measurement.${MeasurementType.WEIGHT}.list`],
    () =>
      MeasurementService.getAll({
        status: Status.ACTIVE,
        type: MeasurementType.WEIGHT,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!(isOnProfilePage && currentUser?.companyId),
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setWeights(response.data);
        }
      },
    }
  );

  const { isFetching: isFetchingRole } = useQuery(
    ["roles.list"],
    () =>
      RoleService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        setRolesList(response.data);
      },
    }
  );

  useQuery(
    ["timezones.list"],
    () =>
      TimezoneService.getAll({
        sortBy: JSON.stringify(["gmtOffset"]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) {
          setTimezones(response.data);
        }
      },
    }
  );

  useQuery(
    ["translate.list"],
    () =>
      TranslateService.getAll({
        status: Status.ACTIVE,
        companyId: "",
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setTranslations(response.data);
        }
      },
    }
  );

  useQuery(
    ["branch.list"],
    () =>
      BranchService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        include: JSON.stringify([{ association: "warehouses" }]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setBranches(response.data);
      },
    }
  );

  const { isFetching: isFetchingProjects } = useQuery(
    ["projects.list"],
    () =>
      ProjectService.getAllNetwork({
        attributes: ["id", "name", "companyId"],
        companyId: currentUser?.companyId,
        group: ["projects.id"],
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: isFMVersion,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setProjectList(response.data.rows);
      },
    }
  );

  const { isFetching: isFetchingOffices } = useQuery(
    ["offices.list"],
    () =>
      OfficeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify(["name"]),
      }),
    {
      enabled: isFMVersion || isSPVersion,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setOfficeList(response.data);
      },
    }
  );

  const handleSetRoleId = (roleId: string) => {
    const role = rolesList?.find((rol) => rol.id === Number(roleId));

    if (role) {
      updateOrCreate(
        userInformation
          ? { ...userInformation, roleId: role.id, role }
          : ({ roleId: role.id, role } as User)
      );
    }
  };

  const selectedProjects = userInformation?.projects ?? [];

  const handleAddAll = () => {
    setSelectedBranches([...branches]);
    updateOrCreate(userInformation ? { ...userInformation, branches } : ({ branches } as User));
  };

  const handleRemoveAll = () => {
    setSelectedBranches([]);

    updateOrCreate(
      userInformation
        ? { ...userInformation, branches: [] }
        : ({ branches: [] as Branch[] } as User)
    );
  };

  const renderChipValue = (selected: number[]) => {
    const selectedBranches: Branch[] = [];

    for (const branchId of selected) {
      const findBranch = branches.find((branch) => branch.id === branchId);

      if (findBranch) selectedBranches.push(findBranch);
    }

    return (
      <Box className={classes.chipBox}>
        {selectedBranches?.map((value: Branch, index: number) => {
          if (index <= 1)
            return <Chip key={value.id} label={value.name} className={classes.chipBranch} />;

          return null;
        })}
      </Box>
    );
  };

  const renderChipValueProjets = () => {
    return (
      <Box className={classes.chipBoxProject}>
        {selectedProjects?.map((value: Project) => {
          return <Chip key={value.id} label={value.name} className={classes.chipBranch} />;
        })}
      </Box>
    );
  };

  const handleSelectedBranch = (branchesId: number[]) => {
    if (branchesId.includes(0)) handleAddAll();
    else if (branchesId.includes(-1)) handleRemoveAll();
    else {
      const branchArr: Branch[] = [];

      for (const branchId of branchesId) {
        const findBranch = branches.find((branch) => branch.id === Number(branchId));

        if (findBranch) branchArr.push(findBranch);
      }
      setSelectedBranches([...branchArr]);
      updateOrCreate(
        userInformation
          ? { ...userInformation, branches: branchArr }
          : ({ branches: branchArr } as User)
      );
    }
  };

  const renderDimensions = () => {
    if (dimensions && dimensions?.length > 0) {
      return dimensions.map((dimension) => {
        if (dimension?.id && dimension?.description && dimension?.code) {
          return (
            <MenuItem data-cy={dimension.code} value={dimension.id}>
              {dimension?.description} {`${dimension?.code ? ` - ${dimension?.code}` : ""}`}
            </MenuItem>
          );
        }

        return null;
      });
    }
  };

  const renderWeights = () => {
    if (weights && weights?.length > 0) {
      return weights.map((weight) => {
        if (weight?.description && weight?.code && weight?.id) {
          return (
            <MenuItem data-cy={weight.code} value={weight.id}>
              {weight.description} {`${weight.code ? ` - ${weight.code}` : ""}`}
            </MenuItem>
          );
        }

        return null;
      });
    }
  };

  const setInformationFromUser = useCallback(
    (user?: User) => {
      if (user) {
        setTimezoneValue(user);
      }
      // TODO uncomment when language is made
      // if (userInformation?.translateId) {
      //   const findTranslation = translations.find(
      //     translation => translation.id === userInformation?.translateId
      //   );
      //   if (findTranslation) {
      //     setLanguage(findTranslation.description);
      //   }
      // }
    },
    [userInformation]
  );

  const managerFullname = useMemo(() => {
    return `${
      userInformation && userInformation?.manager && userInformation?.manager?.name
        ? userInformation?.lastName
          ? `${userInformation?.manager?.name}  ${userInformation?.manager?.lastName || ""}`
          : userInformation?.manager?.name
        : ""
    }`;
  }, [userInformation]);

  const setTimezoneValue = useCallback(
    (user?: User, isRegisteringUser?: boolean) => {
      let timezoneFound;
      const currentTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (user?.timezoneId) {
        timezoneFound = timezones?.find((timezone) => timezone.id === user?.timezoneId);
      } else {
        timezoneFound = timezones?.find((timezone) => timezone.name === currentTimezone);
      }

      if (timezoneFound && timezoneFound?.nameFormatted) {
        const timezone = `GMT (${formatGMT(
          timezoneFound?.gmtOffset
        )}) ${timezoneFound?.nameFormatted?.replace("/", " / ").replace("_", "")}`;

        setTimezone(timezone);
        if (user) {
          if (!user.id && !user?.timezoneId) {
            if (showingFromPeopleSettings || isRegisteringUser) {
              updateOrCreate({
                ...user,
                timezoneId: timezoneFound.id,
              } as User);
            }
          }
        }
      }
    },
    [timezones]
  );

  const isUserUsingCompanyAddress = useCallback((locationId?: number) => {
    if (locationId) {
      if (currentUser && currentUser?.company && currentUser?.company?.location) {
        const companyLocationId = currentUser?.company?.location?.id;

        if (locationId === companyLocationId) setUsingCompanyAddress(true);
      }
    }
  }, []);

  useEffect(() => {
    isUserUsingCompanyAddress(userInformation?.locationId);
    setSelectedBranches(userInformation?.branches || []);
    setLanguage("English (Default)");
    setInformationFromUser(userInformation);
  }, [userInformation]);

  useEffect(() => {
    if (!timezone) setTimezoneValue(userInformation);
  }, [timezones]);

  const isUserDriverType = useMemo(
    () => userInformation?.typeUser === UserTypes.DRIVER,
    [userInformation?.typeUser]
  );

  const renderDimensionsSelect = (companyProfile?: string) => {
    if (companyProfile && companyProfile !== CompanyProfile.MV) return null;

    if (isOnProfilePage) {
      return (
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item xs={12}>
            <section className={classes.sectionDimensions}>
              <p className={classes.sectionTitleDimensions}>{translate("measurements")}</p>
            </section>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <SkeletonComponent isLoading={isFetchingDimensions || isLoading}>
              <Select
                dataCy="dimensions"
                label={translate("dimensions")}
                name="dimensions"
                value={userInformation?.measurementId}
                onChange={(event) => {
                  updateOrCreate({
                    ...(userInformation || {}),
                    measurementId: Number(event.target.value),
                  } as User);
                }}
                displayEmpty
                placeholder={translate("selectAnOption")}
              >
                {renderDimensions()}
              </Select>
            </SkeletonComponent>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <SkeletonComponent isLoading={isFetchingWeights || isLoading}>
              <Select
                dataCy="weight"
                label={translate("weight")}
                name="weight"
                value={userInformation?.measurementWeightId}
                onChange={(event) => {
                  updateOrCreate({
                    ...(userInformation || {}),
                    measurementWeightId: Number(event.target.value),
                  } as User);
                }}
                displayEmpty
                placeholder={translate("selectAnOption")}
              >
                {renderWeights()}
              </Select>
            </SkeletonComponent>
          </Grid>
        </Grid>
      );
    }
  };

  return (
    <div className={classes.settingsMaintainerWrapper}>
      <section className={classes.settingsMaintainerBody}>
        <section className={classes.userMaintainerBodySection}>
          {!isOnProfilePage && (
            <p className={classes.userMaintainerBodySectionTitle}>{translate("contactDetails")}</p>
          )}
          <ContactDetailsComponent
            isOnProfilePage={isOnProfilePage}
            showingFromPeopleSettings={showingFromPeopleSettings}
            registeringUser={isRegisteringUser}
            view={view}
            isLoading={isLoading || false}
            userForm={userInformation || ({} as User)}
            getErrorByField={getErrorByField}
            updateOrCreate={(attribute, value) => {
              updateOrCreate(
                (userInformation && {
                  ...userInformation,
                  [attribute]: value,
                }) ||
                  ({ [attribute]: value } as User)
              );
            }}
          />
        </section>
        {(isRegisteringUser || isRegisteringUser === undefined) && (
          <>
            <section className={classes.userMaintainerBodySection}>
              <p className={classes.userMaintainerBodySectionTitle}>
                {isOnProfilePage ? translate("accountDetails") : translate("userDetails")}
              </p>
              <UserDetailsComponent
                isOnProfilePage={isOnProfilePage}
                showingFromPeopleSettings={showingFromPeopleSettings}
                view={view}
                isLoading={isLoading || false}
                managerName={managerFullname}
                userForm={userInformation || ({} as User)}
                classes={classes}
                sequence={`${sequence?.prefix ? sequence?.prefix : ""}${
                  sequence?.nextValue ? sequence?.nextValue : ""
                }`}
                getErrorByField={getErrorByField}
                updateOrCreate={(attribute, value) => {
                  let info = {
                    ...userInformation,
                    [attribute]: value,
                  } as User;

                  if (attribute === "manager") {
                    info = { ...info, managerId: value?.id ?? null };
                  }

                  updateOrCreate({ ...info });
                }}
                isUserDriverType={isUserDriverType}
                errorResponse={errorResponse}
              />
            </section>

            <section className={classes.userMaintainerBodySection}>
              <p className={classes.userMaintainerBodySectionTitle}>
                {translate("accountPreferences")}
              </p>

              <UserPreferencesComponent
                view={view}
                isLoading={isLoading || false}
                userForm={userInformation || ({} as User)}
                getErrorByField={getErrorByField}
                updateOrCreate={(attribute, value) => {
                  let info = {
                    ...userInformation,
                    [attribute]: value,
                  } as User;

                  if (attribute === "manager") {
                    info = { ...info, managerId: value?.id ?? null };
                  }

                  updateOrCreate({ ...info });
                }}
                errorResponse={errorResponse}
              />
            </section>

            {isFMVersion && userInformation?.typeUser === UserTypes.DRIVER ? (
              <section className="mb-4 pb-3">
                <ShiftTimes
                  shiftTimes={userInformation?.shiftTimes || []}
                  onChange={(shiftTimes: ShiftTime[]) => {
                    updateOrCreate({
                      ...userInformation,
                      shiftTimes,
                    });
                  }}
                />
              </section>
            ) : null}

            {isRetailerPlus(currentUser?.company) && !isOnProfilePage && (
              <section className={classes.userMaintainerBodySection}>
                <p className={classes.userMaintainerBodySectionTitle}>{translate("projects")}</p>

                <Grid
                  item
                  container
                  direction="row"
                  xs={12}
                  sm={12}
                  md={10}
                  lg={10}
                  alignItems="flex-start"
                  spacing={2}
                >
                  <Grid item xs={11} sm={8} md={8} lg={8}>
                    <SkeletonComponent isLoading={isFetchingProjects || isLoading}>
                      <Select
                        dataCy="selectProjects"
                        disabled={view}
                        className={classes.select}
                        name="projectList"
                        value={
                          selectedProjects && selectedProjects?.length
                            ? selectedProjects?.map((project) => project?.id || -2)
                            : []
                        }
                        error={!!getErrorByField("projects")}
                        errorMessage={getErrorByField("projects")}
                        onChange={(event: SelectChangeEvent<any>) => {
                          if (event.target.value.includes(0) || event.target.value.includes(-1))
                            return;
                          const newProjects = projectList
                            ?.filter((project) => event.target.value.includes(project.id))
                            ?.map((project) => ({
                              ...project,
                              companyId:
                                project && project.networks && project.networks.length
                                  ? project?.networks[0]?.associatedCompanyId
                                  : project?.companyId,
                            }));

                          updateOrCreate({
                            ...userInformation,
                            projects: newProjects,
                          } as User);
                        }}
                        input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                        multiple
                        renderValue={() => renderChipValueProjets()}
                      >
                        {selectedProjects.length < projectList?.length && (
                          <MenuItem
                            value={0}
                            onClick={() => {
                              updateOrCreate({
                                ...userInformation,
                                projects: projectList,
                              } as User);
                            }}
                          >
                            <span className={classes.listSpan}>
                              <AddCircleOutline className="mr-3" />
                              {translate("addAll")}
                            </span>
                          </MenuItem>
                        )}
                        {selectedProjects.length === projectList?.length && (
                          <MenuItem
                            value={-1}
                            onClick={() => {
                              updateOrCreate({
                                ...userInformation,
                                projects: [],
                              } as User);
                            }}
                          >
                            <span className={classes.removeListSpan}>
                              <RemoveCircleOutline className="mr-3" />
                              {translate("removeAll")}
                            </span>
                          </MenuItem>
                        )}
                        {projectList.map((project) => (
                          <MenuItem data-cy={project.name} value={project.id} key={project.id}>
                            {project.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </SkeletonComponent>
                  </Grid>
                  {projectList.length > 0 && (
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                      <Chip label={selectedProjects.length} className={classes.chipLabelProject} />
                    </Grid>
                  )}
                </Grid>
              </section>
            )}

            {(isFMVersion || isSPVersion) && !isOnProfilePage && (
              <section className={classes.userMaintainerBodySection}>
                <p className={classes.userMaintainerBodySectionTitle}>{translate("offices")}</p>

                <Grid
                  item
                  container
                  direction="row"
                  xs={12}
                  sm={12}
                  md={10}
                  lg={10}
                  alignItems="flex-start"
                  spacing={2}
                >
                  <Grid item xs={11} sm={8} md={8} lg={8}>
                    <SelectMultiple
                      loading={isFetchingOffices || isLoading}
                      dataCy="selectOffices"
                      disabled={view}
                      className={classes.select}
                      name="officeList"
                      onChange={(event: SelectChangeEvent<any>) => {
                        if (event.target.value.includes(0) || event.target.value.includes(-1))
                          return;
                        const officesSelected = officeList?.filter((office) =>
                          event.target.value.includes(office.id)
                        );

                        updateOrCreate({
                          ...userInformation,
                          offices: officesSelected,
                        } as User);
                      }}
                      value={userInformation?.offices?.map((office) => Number(office?.id)) || []}
                      onAddAll={() => {
                        updateOrCreate({
                          ...userInformation,
                          offices: officeList,
                        } as User);
                      }}
                      onRemoveAll={() => {
                        updateOrCreate({
                          ...userInformation,
                          offices: [],
                        } as User);
                      }}
                      renderInternalCount={false}
                      items={officeList.map((office) => ({
                        key: Number(office.id),
                        label: office.name,
                      }))}
                      selecteds={
                        userInformation?.offices?.map((office) => ({
                          key: Number(office.id),
                          label: office.name,
                        })) || []
                      }
                    />
                  </Grid>
                  <Grid item xs={1} sm={1} md={1} lg={1}>
                    <SelectMultipleCount
                      items={officeList.map((office) => ({
                        key: Number(office.id),
                        label: office.name,
                      }))}
                      selecteds={
                        userInformation?.offices?.map((office) => ({
                          key: Number(office.id),
                          label: office.name,
                        })) || []
                      }
                    />
                  </Grid>
                </Grid>
              </section>
            )}

            {!isOnProfilePage && (
              <section className={classes.userMaintainerBodySection}>
                <p className={`${classes.userMaintainerBodySectionTitle} mb-0`}>
                  {translate("role")}
                </p>
                {getErrorByField("roleId") && (
                  <FormHelperText className={classes.formHelper}>
                    <InfoOutlined htmlColor="#d92d20" className={classes.iconError} />
                    {getErrorByField("roleId")}
                  </FormHelperText>
                )}
                <SkeletonComponent isLoading={isFetchingRole || false}>
                  <>
                    {rolesList && (
                      <RadioGroup
                        value={userInformation?.roleId}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleSetRoleId(e.target.value)
                        }
                        options={rolesList.map((role) => ({
                          labelWithTooltip: (
                            <TooltipRoleName
                              title={role.description || ""}
                              child={<span>{role.name}</span>}
                            />
                          ),
                          value: role.id,
                          "data-cy": role.name,
                          disabled: view,
                        }))}
                      />
                    )}
                  </>
                </SkeletonComponent>
              </section>
            )}

            <section className={classes.userMaintainerBodySection}>
              <p className={classes.userMaintainerBodySectionTitle}>{translate("address")}</p>
              <Grid container direction="column">
                <Grid item md={isOnProfilePage ? 6 : 12} lg={isOnProfilePage ? 6 : 12}>
                  <SkeletonComponent isLoading={isLoading || false}>
                    <UserAddressComponent
                      userForm={userInformation || ({} as User)}
                      view={view}
                      isUsingCompanyAddress={isUsingCompanyAddress}
                      setUsingCompanyAddress={setUsingCompanyAddress}
                      updateOrCreate={(user: User) => {
                        updateOrCreate({ ...user });
                      }}
                    />
                  </SkeletonComponent>
                </Grid>
              </Grid>
            </section>
            <section className={classes.userMaintainerBodySection}>
              <p className={classes.userMaintainerBodySectionTitle}>{translate("password")}</p>
              <Grid container direction="column">
                <PasswordContainer>
                  <LeftTextContainer>
                    <TextLeft>{translate("resetPassword")}</TextLeft>
                    <TextLeftBottom>
                      {translate(
                        "weWillSendALinkToResetYourPasswordToTheEmailAddressAssociatedWithYourAccount"
                      )}
                    </TextLeftBottom>
                  </LeftTextContainer>
                  <TextRightContainer onClick={() => forgotPassword()}>
                    <StyledImage src={KEY_ICON} />
                    <TextRight>{translate("resetPassword")}</TextRight>
                  </TextRightContainer>
                </PasswordContainer>
              </Grid>
            </section>

            <div className={classes.customHr}></div>
            <section className={classes.userMaintainerBodySection}>
              <Grid container direction="row" alignItems="flex-start" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={showingFromPeopleSettings ? (isOnProfilePage ? 6 : 4) : 6}
                  lg={showingFromPeopleSettings ? (isOnProfilePage ? 6 : 4) : 6}
                >
                  <SkeletonComponent isLoading={isLoading || false}>
                    <>
                      <AutoSuggest
                        label={translate("timezone")}
                        disabled={view}
                        placeholder={translate("searchForTimezone")}
                        inputValue={timezone}
                        options={timezones}
                        dataCy="timezone"
                        error={!!getErrorByField("timezoneId")}
                        errorMessage={getErrorByField("timezoneId")}
                        getOptionLabel={(time: Timezone) =>
                          time && time.gmtOffset && time.nameFormatted
                            ? `GMT (${formatGMT(time?.gmtOffset)}) ${time?.nameFormatted
                                ?.replace("/", " / ")
                                .replace("_", "")}`
                            : ""
                        }
                        getOptionSelected={(option: Timezone, value: Timezone) =>
                          option && value && option.id === value.id
                        }
                        onChange={(value: Timezone) => {
                          if (value) {
                            updateOrCreate(
                              (userInformation && {
                                ...userInformation,
                                timezoneId: value.id,
                              }) ||
                                ({ timezoneId: value.id } as User)
                            );
                          } else {
                            updateOrCreate(
                              userInformation
                                ? { ...userInformation, timezoneId: undefined }
                                : ({ timezoneId: undefined } as User)
                            );
                          }
                        }}
                      />
                      <span className={classes.userMaintainerTimezoneMessage}>
                        {translate("timezoneIsUpdatedAutomaticallyToMatchYourComputerTimezone")}
                      </span>
                    </>
                  </SkeletonComponent>
                </Grid>

                {currentUser?.company?.profile?.id !== 4 &&
                  showingFromPeopleSettings &&
                  !isOnProfilePage && (
                    <>
                      <Grid item xs={12} sm={4} md={3} lg={3}>
                        <SkeletonComponent isLoading={isLoading || false}>
                          <AutoSuggest
                            label={translate("language")}
                            disabled={true}
                            inputValue={language}
                            data-cy="language"
                            options={translations || []}
                            getOptionLabel={(language: Translate) =>
                              language && language.description ? language.description : ""
                            }
                            getOptionSelected={(option: Translate, value: Translate) =>
                              option && value && option.id === value.id
                            }
                            onChange={(value: Translate) => {
                              if (value) {
                                updateOrCreate(
                                  userInformation
                                    ? {
                                        ...userInformation,
                                        translateId: value.id,
                                      }
                                    : ({ translateId: value.id } as User)
                                );
                              }
                            }}
                          />
                        </SkeletonComponent>
                      </Grid>

                      {currentUser?.company?.profile?.id === 3 && (
                        <Grid
                          item
                          container
                          direction="row"
                          xs={12}
                          sm={4}
                          md={3}
                          lg={3}
                          alignItems="flex-end"
                          spacing={1}
                        >
                          <Grid
                            item
                            xs={selectedBranches.length > 0 ? 11 : 12}
                            sm={11}
                            md={11}
                            lg={11}
                          >
                            <SkeletonComponent isLoading={isLoading || false}>
                              <Select
                                dataCy="selectBranch"
                                disabled={view}
                                className={classes.select}
                                label="Branch"
                                name="branchList"
                                value={
                                  selectedBranches && selectedBranches?.length
                                    ? selectedBranches?.map((branch) => branch?.id || -2)
                                    : []
                                }
                                error={!!getErrorByField("branches")}
                                errorMessage={getErrorByField("branches")}
                                onChange={(event: SelectChangeEvent<any>) =>
                                  handleSelectedBranch(event.target.value)
                                }
                                input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                                multiple
                                renderValue={(selected) => renderChipValue(selected as number[])}
                              >
                                {selectedBranches.length < branches?.length && (
                                  <MenuItem value={0}>
                                    <span className={classes.listSpan}>
                                      <AddCircleOutline className="mr-3" />
                                      {translate("addAll")}
                                    </span>
                                  </MenuItem>
                                )}
                                {selectedBranches.length === branches?.length && (
                                  <MenuItem value={-1}>
                                    <span className={classes.removeListSpan}>
                                      <RemoveCircleOutline className="mr-3" />
                                      {translate("removeAll")}
                                    </span>
                                  </MenuItem>
                                )}
                                {branches.map((branch) => (
                                  <MenuItem data-cy={branch.name} value={branch.id} key={branch.id}>
                                    {branch.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </SkeletonComponent>
                          </Grid>
                          {selectedBranches.length > 0 && (
                            <Grid item xs={1} sm={1} md={1} lg={1}>
                              <Chip label={selectedBranches.length} className={classes.chipLabel} />
                            </Grid>
                          )}
                        </Grid>
                      )}
                    </>
                  )}
              </Grid>
              {renderDimensionsSelect(currentUser?.company?.profile?.initials)}
            </section>
          </>
        )}
      </section>
    </div>
  );
};

export default SettingsUserForm;
